import { createRef, PureComponent } from "react";
import { clsx } from "clsx";

import { logger } from "~/utils/logging";
import { throttle } from "~/utils/helpers";

import { Button } from "../button/Button";
import { VideoControls } from "./VideoControls";
import { VideoShortcuts } from "./VideoShortcuts";
import { Track } from "../media/Track";
import { VideoElement } from "./VideoElement";
import { getMediaElementProperties, load } from "../media/helpers";
import { MediaPlayerContext } from "../media/context";
import { initialState, reducer } from "../media/state";
import { Translation } from "../i18n/Translation";
import {
  MediaPlayerFullscreenWindowPosition,
  MediaPlayerViewMode,
} from "../media/types";
import { MinimalVideoControls } from "./MinimalVideoControls";
import { Spinner } from "../spinner/Spinner";
import { MediaError } from "../media/errors";
import {
  SHORT_SKIP_SECONDS,
  LONG_SKIP_SECONDS,
  CONTROLS_ACTIVE_SECONDS,
  DEFAULT_THROTTLE,
  CAPTION_CHANGE,
  PAUSE,
  PLAY,
  LOADED_META_DATA,
  LOADED_DATA,
  USER_ACTIVITY,
  LOAD_START,
  WAITING,
  CAN_PLAY,
  CAN_PLAY_THROUGH,
  PLAYING,
  END,
  SEEKED,
  TIME_UPDATE,
  PROGRESS_CHANGE,
  VOLUME_CHANGE,
  VIEWMODE_CHANGE,
  RATE_CHANGE,
  PLAYBACK_RATE_OPTIONS,
  AIRPLAYING_CHANGE,
  CAN_AIRPLAY,
  PLAYLIST_POSITION_CHANGE,
  DURATION_CHANGE,
  SEEKING_TIME,
  SEEKING,
  VIEWMODE_FULLSCREEN_SLIDES_CHANGE,
  VIEWMODE_FULLSCREEN_WINDOW_POSITION_CHANGE,
  PlayState,
} from "../media/constants";

import "./Video.css";

import type {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  FocusEvent as ReactFocusEvent,
  MutableRefObject,
  ReactNode,
  SyntheticEvent,
} from "react";
import type { Hls } from "@mux/playback-core";
import type { MediaPlayerAction, MediaChapter } from "../media/types";
import type { MediaPlayerContextValue } from "../media/context";
import type { MediaElementState, MediaPlayerState } from "../media/state";
import type { CaptionsProp } from "../media/Track";

export interface VideoPlaylistEntry {
  captions?: CaptionsProp[];
  poster?: string;
  src: string;
  type: VideoPlaylistEntryType;
}

export interface VideoProps {
  autoplay?: boolean;
  captions?: CaptionsProp[];
  chapters?: MediaChapter[];
  children?: ReactNode;
  contextRef?: MutableRefObject<MediaPlayerContextValue | null>;
  startTime?: number;
  playState?: PlayState;
  playbackRate?: number;
  volume?: number;
  captionsEnabled?: boolean;
  src?: string;
  poster?: string;
  playlist?: Array<VideoPlaylistEntry>;
  title: string;
  windowPosition?: MediaPlayerFullscreenWindowPosition;
  onAirplayingChange?: (airplaying: boolean) => void;
  onCaptionChange?: (enabled: boolean) => void;
  onEnded?: () => void;
  onError?: () => void;
  onFullscreenEnter?: () => void;
  onFullscreenLeave?: () => void;
  onPaused?: () => void;
  onPlaylistItem?: (item?: VideoPlaylistEntry) => void;
  onPlaying?: () => void;
  onPlayStateChange?: (playState: PlayState) => void;
  onRateChange?: (playbackRate: number) => void;
  onVolumeChange?: (volume: number) => void;
  onPictureInPictureEnter?: () => void;
  onPictureInPictureLeave?: () => void;
  onReplay?: () => void;
  onTimeUpdate?: (timestamp: number) => void;
  onViewModeChange?: (viewMode: MediaPlayerViewMode) => void;
  onViewModeFullscreenSlidesChange?: (
    viewModeFullscreenSlides: boolean,
  ) => void;
  onViewModeFullscreenWindowPositionChange?: (
    viewModeFullscreenWindowPosition: MediaPlayerFullscreenWindowPosition,
  ) => void;
}

export enum VideoPlaylistEntryType {
  FEATURE,
  BUMPER,
}

// TODO: Calculate these values
const PRE_ROLL_VIDEO_PLAYLIST_POSITION = 0;
const SESSION_VIDEO_PLAYLIST_POSITION = 1;
const POST_ROLL_VIDEO_PLAYLIST_POSITION = 2;
const PLAY_STATE_PLAYLIST_POSITION: Map<PlayState, number> = new Map([
  [PlayState.PLAYING_PRE_ROLL, PRE_ROLL_VIDEO_PLAYLIST_POSITION],
  [PlayState.PLAYING_SESSION, SESSION_VIDEO_PLAYLIST_POSITION],
  [PlayState.PLAYING_POST_ROLL, POST_ROLL_VIDEO_PLAYLIST_POSITION],
  [PlayState.ENDED, POST_ROLL_VIDEO_PLAYLIST_POSITION],
]);

// Duration in which the loading state is fading out, so we
// delay updating watchview playstate
const PLAY_STATE_UPDATE_DELAY = 250;

// When to preload the freeze-frame image between playlist
// images
const PRELOAD_LOADING_POSTER_THRESHOLD = 1;

// Don't seek past duration - margin so that the session
// ends instead of replaying.
const MAX_SEEK_THRESHOLD = 0.1;

// How long to wait before loading next video (miliseconds)
const PLAYLIST_CHANGE_WAIT = 500;

// VTTCue settings. We set this by js, to avoid having to
// specify in the .vtt files
const VTT_TOP_CUE_LINE = 15;
const VTT_CUE_SNAP_TO_LINES = false;

export class Video extends PureComponent<VideoProps, MediaPlayerState> {
  ref = createRef<HTMLDivElement>();
  mediaElementRef = createRef<HTMLVideoElement>();

  hls: Hls | null = null;

  controlsVisibleTimer: ReturnType<typeof setTimeout> | null = null;

  state: MediaPlayerState = {
    ...initialState,
    playlistPosition: !this.props.playlist
      ? 0
      : PLAY_STATE_PLAYLIST_POSITION.get(
          this.props.playState ?? PlayState.PLAYING_PRE_ROLL,
        ) || 0,
    captionsEnabled: this.props.captionsEnabled ?? false,
    viewModeFullscreenWindowPosition:
      this.props.windowPosition ??
      initialState.viewModeFullscreenWindowPosition,
  };

  static defaultProps = {
    startTime: 0,
    playbackRate: 1,
    volume: 1,
    playState: PlayState.PLAYING_SESSION,
    chapters: undefined,
    captionsEnabled: false,
  };

  get video() {
    return this.mediaElementRef.current ?? null;
  }

  get currentPlaylistItem() {
    return this.props.playlist?.[this.state.playlistPosition];
  }

  get videoType() {
    return this.currentPlaylistItem?.type ?? VideoPlaylistEntryType.FEATURE;
  }

  get videoSrc() {
    return this.props.src ?? this.currentPlaylistItem?.src;
  }

  get poster() {
    const { poster, playlist } = this.props;
    const { currentTime, playlistPosition } = this.state;

    // Use the poster prop if one is provided
    if (poster) return this.props.poster;

    // Eject if a playlist isn't provided
    if (!playlist) return undefined;

    // "Preload" if the video is about to end
    const shouldPreload =
      currentTime >
      this.mediaElementProperties.duration - PRELOAD_LOADING_POSTER_THRESHOLD;

    const curr = playlist?.[playlistPosition];
    const next = playlist?.[playlistPosition + 1];

    return shouldPreload ? next?.poster : curr?.poster;
  }

  get loadTime() {
    if (
      this.props.playState === PlayState.ENDED &&
      this.state.playlistPosition === POST_ROLL_VIDEO_PLAYLIST_POSITION
    ) {
      return Math.round(this.video?.duration ?? 0);
    }

    return this.props.startTime ?? 0;
  }

  get mediaElementProperties() {
    if (!this.video) {
      return {} as MediaElementState;
    }

    return getMediaElementProperties(this.video);
  }

  get muted() {
    return this.video ? this.video.muted : this.state.muted;
  }

  set muted(val) {
    if (this.video) {
      this.video.muted = val;
    }
  }

  componentDidMount() {
    this.load();

    document.addEventListener("mousedown", this.handleOutsideClick);

    document.addEventListener("fullscreenchange", this.handleFullscreenChange);

    this.ref.current?.addEventListener("touchstart", this.handleMouseDown, {
      passive: false,
    });

    this.ref.current?.addEventListener("mousedown", this.handleMouseDown, {
      passive: false,
    });

    this.video?.addEventListener(
      "leavepictureinpicture",
      this.handleLeavePictureInPicture,
    );

    this.video?.addEventListener(
      "webkitplaybacktargetavailabilitychanged",
      this.handlePlaybackTargetAvailabilityChanged,
    );

    this.video?.addEventListener(
      "webkitcurrentplaybacktargetiswirelesschanged",
      this.handlePlaybackTargetIsWirelessChanged,
    );

    try {
      this.video?.textTracks.addEventListener("addtrack", this.handleTrackLoad);

      this.video?.textTracks.addEventListener(
        "removetrack",
        this.handleTrackLoad,
      );
    } catch (error) {}

    this.initializeCaptions();
  }

  componentDidUpdate(prevProps: VideoProps, prevState: MediaPlayerState) {
    // src change
    if (prevProps.src != this.props.src) {
      this.load();
    }

    // playlist change
    if (
      JSON.stringify(prevProps.playlist) !== JSON.stringify(this.props.playlist)
    ) {
      this.reload();
    }

    if (
      prevState.viewMode !== this.state.viewMode ||
      prevState.viewModeFullscreenSlides !==
        this.state.viewModeFullscreenSlides ||
      prevState.viewModeFullscreenWindowPosition !==
        this.state.viewModeFullscreenWindowPosition
    ) {
      this.setCaptionPosition();
    }
  }

  componentWillUnmount() {
    this.cleanUp();

    document.removeEventListener("mousedown", this.handleOutsideClick);

    this.ref.current?.removeEventListener("touchstart", this.handleMouseDown);

    this.ref.current?.removeEventListener("mousedown", this.handleMouseDown);

    this.video?.removeEventListener(
      "leavepictureinpicture",
      this.handleLeavePictureInPicture,
    );

    this.video?.removeEventListener(
      "webkitplaybacktargetavailabilitychanged",
      this.handlePlaybackTargetAvailabilityChanged,
    );

    this.video?.removeEventListener(
      "webkitcurrentplaybacktargetiswireless",
      this.handlePlaybackTargetIsWirelessChanged,
    );

    try {
      this.video?.textTracks.removeEventListener(
        "addtrack",
        this.handleTrackLoad,
      );

      this.video?.textTracks.removeEventListener(
        "removetrack",
        this.handleTrackLoad,
      );
    } catch (error) {}
  }

  dispatch = async (action: Partial<MediaPlayerAction>) => {
    return new Promise<MediaPlayerState>((resolve) => {
      this.setState(
        (state: MediaPlayerState) =>
          reducer(state, {
            ...action,
            mediaElementProperties: this.mediaElementProperties,
          }),
        () => {
          this.sideEffect(action);

          return resolve(this.state);
        },
      );
    });
  };

  /**
   *  State update side-effects
   */
  sideEffect(action: Partial<MediaPlayerAction>) {
    const bumperAllowList = [LOAD_START, END, VIEWMODE_CHANGE];

    const skip =
      this.videoType === VideoPlaylistEntryType.BUMPER &&
      !bumperAllowList.includes(action.type!);

    if (skip) return;

    switch (action.type) {
      case TIME_UPDATE:
      case SEEKED:
        this.props.onTimeUpdate?.(this.state.currentTime);
        break;
      case RATE_CHANGE:
        this.props.onRateChange?.(this.state.playbackRate);
        break;
      case VOLUME_CHANGE:
        this.props.onVolumeChange?.(this.state.volume);
        break;
      case PAUSE:
        this.props.onPaused?.();
        break;
      case PLAYING:
        this.props.onPlaying?.();
        break;
      case CAPTION_CHANGE:
        this.props.onCaptionChange?.(action.payload || false);
        break;
      case AIRPLAYING_CHANGE:
        this.props.onAirplayingChange?.(action.payload || false);
        break;
      case END:
        this.props.onPlayStateChange?.(PlayState.ENDED);
        break;
      case VIEWMODE_CHANGE:
        this.props.onViewModeChange?.(
          action.payload || MediaPlayerViewMode.inline,
        );
        break;
      case VIEWMODE_FULLSCREEN_SLIDES_CHANGE:
        this.props.onViewModeFullscreenSlidesChange?.(action.payload ?? false);
        break;
      case VIEWMODE_FULLSCREEN_WINDOW_POSITION_CHANGE:
        this.props.onViewModeFullscreenWindowPositionChange?.(
          action.payload ?? MediaPlayerFullscreenWindowPosition.bottomLeft,
        );
        break;
      case LOAD_START:
        if (this.props.playlist) {
          setTimeout(
            () =>
              this.props.onPlayStateChange?.(
                PLAY_STATE_PLAYLIST_POSITION.get(this.state.playlistPosition) ??
                  PlayState.PLAYING_SESSION,
              ),
            PLAY_STATE_UPDATE_DELAY,
          );
        }
        break;
      default:
        break;
    }
  }

  cleanUp() {
    try {
      if (this.hls) {
        this.hls.detachMedia();
        this.hls.stopLoad();
        this.hls.destroy();
      }
    } catch (error) {}
  }

  async load() {
    if (!this.video) return;

    // Clean-up previous instance
    this.cleanUp();

    // Load the media
    this.hls = await load(this.video, {
      src: this.videoSrc,
    }).catch((error) => {
      logger.error("Video player failed to load media", error);
      return null;
    });

    this.loadMediaState();
    this.setCaptionPosition();

    if (this.currentPlaylistItem) {
      this.props.onPlaylistItem?.(this.currentPlaylistItem);
    }
  }

  loadMediaTime = () => {
    // Skip loading time if below delta.
    if (Math.abs(this.loadTime - (this.video?.currentTime ?? 0)) < 5) return;

    this.video!.currentTime = this.loadTime;
  };

  loadMediaState = () => {
    // Set start time
    this.loadMediaTime();

    if (this.video) {
      // Set playback rate
      this.video.playbackRate = this.props.playbackRate ?? 1;

      // Set volume
      this.video.volume = this.props.volume ?? 1;
    }
  };

  reload() {
    if (!this.video) return;

    this.setPlaylistPosition(
      this.loadTime
        ? SESSION_VIDEO_PLAYLIST_POSITION
        : PRE_ROLL_VIDEO_PLAYLIST_POSITION,
    );

    // Seek to start time
    this.video.currentTime = this.loadTime;

    // Set playback rate
    this.video.playbackRate = this.props.playbackRate ?? 1;

    // Set volume
    this.video.volume = this.props.volume ?? 1;
  }

  autoplay() {
    const promise = this.video?.play?.();

    if (promise !== undefined) {
      promise
        .then(() => {
          this.dispatch({ type: PLAY });
        })
        .catch(() => {
          // Re-try, but muted
          if (!this.muted) {
            this.setState({ hasUnmutedAutoplay: false });
            this.muted = true;
            const mutedPromise = this.video?.play?.();
            mutedPromise
              ?.then(() => {
                this.dispatch({ type: PLAY });
              })
              .catch(() => {
                // Show controls as last resort
                this.showControls();
              });
          }
        });
    }
  }

  play() {
    try {
      this.video?.play();
      this.dispatch({ type: PLAY });
    } catch (error) {}
  }

  pause() {
    const promise = this.video?.play?.();

    if (promise !== undefined) {
      try {
        promise.then(() => this.video?.pause());
        this.dispatch({ type: PAUSE });
      } catch (error) {}
    }
  }

  backward(seconds: number = SHORT_SKIP_SECONDS) {
    this.seek(this.state.currentTime - seconds);
  }

  forward(seconds: number = LONG_SKIP_SECONDS) {
    this.seek(this.state.currentTime + seconds);
  }

  seek(time: number) {
    if (!this.state.hasStarted) return;
    try {
      this.video!.currentTime = Math.max(
        0,
        Math.min(this.video!.duration - MAX_SEEK_THRESHOLD, time),
      );
      this.dispatch({ type: SEEKED });
    } catch (error) {
      logger.error(`Video player "seek" failed`, error);
    }
  }

  setPlaybackRate(playbackRate: number) {
    try {
      this.video!.playbackRate = playbackRate;
      this.dispatch({ type: RATE_CHANGE, payload: playbackRate });
    } catch (error) {
      logger.error(`Video player "setPlaybackRate" failed`, error);
    }
  }

  setVolume(volume: number) {
    try {
      this.video!.volume = volume;
      this.dispatch({ type: VOLUME_CHANGE, payload: volume });
    } catch (error) {
      logger.error(`Video player "setVolume" failed`, error);
    }
  }

  setPlaylistPosition(position: number) {
    this.dispatch({
      type: PLAYLIST_POSITION_CHANGE,
      payload: position,
    }).then(() => {
      this.load();
    });
  }

  increasePlaybackRate() {
    const currRateIndex = PLAYBACK_RATE_OPTIONS.indexOf(
      this.state.playbackRate,
    );
    const newRate =
      PLAYBACK_RATE_OPTIONS[
        Math.min(PLAYBACK_RATE_OPTIONS.length - 1, currRateIndex + 1)
      ];
    this.setPlaybackRate(newRate);
  }

  decreasePlaybackRate() {
    const currRateIndex = PLAYBACK_RATE_OPTIONS.indexOf(
      this.state.playbackRate,
    );
    const newRate = PLAYBACK_RATE_OPTIONS[Math.max(0, currRateIndex - 1)];
    this.setPlaybackRate(newRate);
  }

  toggleCaptions() {
    setTimeout(() => {
      this.dispatch({
        type: CAPTION_CHANGE,
        payload: !this.state.captionsEnabled,
      });
    }, 1);
  }

  initializeCaptions() {
    [...(this.video?.textTracks ?? [])].forEach((t) => {
      if (t.kind == "captions" && t.label == "trackComponent") {
        t.mode = "hidden";
      } else {
        // Disable baked-in captions
        t.mode = "disabled";
      }
    });
  }

  setCaptionMode() {
    [...(this.video?.textTracks ?? [])].forEach((t) => {
      if (t.kind == "captions" && t.label == "trackComponent") {
        t.mode = this.state.captionsEnabled ? "showing" : "hidden";
      }
    });
  }

  setCaptionPosition() {
    try {
      if (!this.video?.textTracks) return;

      const showCueOnTop =
        this.state.viewMode == MediaPlayerViewMode.fullscreen &&
        !this.state.viewModeFullscreenSlides &&
        (this.state.viewModeFullscreenWindowPosition ==
          MediaPlayerFullscreenWindowPosition.bottomLeft ||
          this.state.viewModeFullscreenWindowPosition ==
            MediaPlayerFullscreenWindowPosition.bottomRight);

      const cueLine = showCueOnTop ? VTT_TOP_CUE_LINE : "auto";

      [...this.video.textTracks].forEach((t) => {
        const { cues } = t;
        if (cues) {
          if ((cues[0] as VTTCue).line !== cueLine) {
            for (let i = 0; i < cues.length; ++i) {
              (cues[i] as VTTCue).line = cueLine;
              (cues[i] as VTTCue).snapToLines = VTT_CUE_SNAP_TO_LINES;
            }
          }
        }
      });
    } catch (error) {}
  }

  setAirplaying(airplaying: boolean) {
    this.dispatch({
      type: AIRPLAYING_CHANGE,
      payload: airplaying,
    });
  }

  setCanAirplay(canAirplay: boolean) {
    this.dispatch({
      type: CAN_AIRPLAY,
      payload: canAirplay,
    });
  }

  setViewMode = (viewMode?: MediaPlayerViewMode) => {
    if (viewMode === MediaPlayerViewMode.freestyle) {
      if (!document.pictureInPictureElement) {
        try {
          this.video?.requestPictureInPicture();
          this.props.onPictureInPictureEnter?.();
        } catch {
          this.handleLeavePictureInPicture();
        }
      }
    } else if (document.pictureInPictureElement) {
      document.exitPictureInPicture();
    }

    if (viewMode === MediaPlayerViewMode.fullscreen) {
      this.props.onFullscreenEnter?.();
    }

    if (this.state.viewMode === MediaPlayerViewMode.fullscreen) {
      this.props.onFullscreenLeave?.();
    }

    this.dispatch({
      type: VIEWMODE_CHANGE,
      payload: viewMode,
    });
  };

  setViewModeFullscreenSlides = (viewModeFullscreenSlides: boolean) => {
    this.dispatch({
      type: VIEWMODE_FULLSCREEN_SLIDES_CHANGE,
      payload: viewModeFullscreenSlides,
    });
  };

  setViewModeFullscreenWindowPosition = (
    viewModeFullscreenWindowPosition: MediaPlayerFullscreenWindowPosition,
  ) => {
    this.dispatch({
      type: VIEWMODE_FULLSCREEN_WINDOW_POSITION_CHANGE,
      payload: viewModeFullscreenWindowPosition,
    });
  };

  setSeeking = (seeking: boolean) => {
    this.dispatch({
      type: SEEKING,
      payload: seeking,
    });
  };

  setSeekingTime = (seekingTime: number) => {
    this.dispatch({
      type: SEEKING_TIME,
      payload: seekingTime,
    });
  };

  showControls() {
    this.dispatch({ type: USER_ACTIVITY, payload: true });
  }

  hideControls() {
    this.dispatch({ type: USER_ACTIVITY, payload: false });
  }

  startControlsTimer() {
    this.showControls();

    this.clearControlsTimer();

    this.controlsVisibleTimer = setTimeout(() => {
      this.hideControls();
    }, CONTROLS_ACTIVE_SECONDS * 1000);
  }

  clearControlsTimer() {
    if (this.controlsVisibleTimer) {
      clearTimeout(this.controlsVisibleTimer);
    }
  }

  //////////////////////////////
  // Container event handlers //
  //////////////////////////////

  handleFullscreenChange = () => {
    if (!globalThis.document.fullscreenElement) {
      this.setViewMode(MediaPlayerViewMode.inline);
    }
  };

  handleOutsideClick = (e: MouseEvent) => {
    if (!this.ref.current?.contains(e.target as Node)) {
      this.hideControls();
      this.clearControlsTimer();
    }
  };

  handleMouseDown = throttle(
    (e: ReactMouseEvent<HTMLDivElement> | ReactTouchEvent<HTMLDivElement>) => {
      // Hide the controls if clicking a second time while
      // the controls are active
      const allowList = ["VideoControls__playback", "VideoControls__utils"];
      const classList = Array.from(
        (e.target as HTMLDivElement).classList.values(),
      );
      const shouldHideControls =
        this.state.userActivity && classList.some((v) => allowList.includes(v));

      if (shouldHideControls) {
        this.hideControls();
        this.clearControlsTimer();
      } else {
        if (!this.state.muted && !this.state.userActivity) {
          // If controls are hidden, don't allow click to
          // propogate.
          e.preventDefault();
        }
        this.startControlsTimer();
      }
    },
    DEFAULT_THROTTLE,
  );

  handleMouseMove = throttle((e: ReactMouseEvent<HTMLDivElement>) => {
    const totalMovement = Math.abs(e.movementX) + Math.abs(e.movementY);
    if (!totalMovement) return;
    this.startControlsTimer();
  }, DEFAULT_THROTTLE);

  handleMouseUp = () => {};

  handleKeyDown = () => {
    this.startControlsTimer();
  };

  handleFocus = (e: ReactFocusEvent) => {
    // Previously-focused element
    if (e.relatedTarget?.classList?.contains("VideoControl")) return;

    this.startControlsTimer();
  };

  handleBlur = () => {};

  /////////////////////////////////////
  // HTMLMediaElement event handlers //
  /////////////////////////////////////

  handleLoadStart = () => {
    this.dispatch({ type: LOAD_START });

    // Autoplay
    if (this.props.autoplay) {
      this.autoplay();
    } else {
      this.pause();
      this.showControls();
    }
  };

  handleDurationChange = () => {
    this.dispatch({ type: DURATION_CHANGE });
  };

  handleWaiting = () => {
    this.dispatch({ type: WAITING });
  };

  handleCanPlay = () => {
    this.dispatch({ type: CAN_PLAY });
  };

  handleCanPlayThrough = () => {
    this.dispatch({ type: CAN_PLAY_THROUGH });
  };

  handlePlaying = () => {
    this.dispatch({ type: PLAYING });
  };

  handleProgress = () => {
    this.dispatch({ type: PROGRESS_CHANGE });
  };

  handleEnded = () => {
    this.dispatch({ type: TIME_UPDATE });

    if (
      this.props.playlist &&
      this.state.playlistPosition < this.props.playlist.length - 1
    ) {
      // Show loading freeze frame
      this.dispatch({ type: LOAD_START });

      setTimeout(() => {
        this.setPlaylistPosition(this.state.playlistPosition + 1);
      }, PLAYLIST_CHANGE_WAIT);
    } else {
      this.dispatch({ type: END });
      this.startControlsTimer();
      this.props.onEnded?.();
    }
  };

  handleError = (event: SyntheticEvent) => {
    const mediaEl = event.target as HTMLMediaElement;

    // Safari sometimes throws an error event with a null error.
    if (!mediaEl?.error) return;

    const { message, code } = mediaEl.error;

    const error = new MediaError(message, code);

    logger.error("Video Error", error, {
      code: error.code,
      message: error.message,
      currentTime: mediaEl.currentTime,
    });

    this.props.onError?.();
  };

  handleLoadedMetaData = () => {
    this.dispatch({ type: LOADED_META_DATA });
  };

  handleLoadedData = () => {
    this.dispatch({ type: LOADED_DATA });
  };

  handleTimeUpdate = () => {
    if (!this.state.hasStarted) return;
    this.dispatch({ type: TIME_UPDATE });
  };

  handleVolumeChange = () => {
    this.dispatch({ type: VOLUME_CHANGE });
  };

  handleLeavePictureInPicture = () => {
    this.setViewMode(MediaPlayerViewMode.inline);

    this.props.onPictureInPictureLeave?.();
  };

  handlePause = () => {
    this.dispatch({ type: PAUSE });
  };

  handlePlaybackTargetAvailabilityChanged = (event: any) => {
    this.setCanAirplay(event.availability === "available");
  };

  handlePlaybackTargetIsWirelessChanged = () => {
    const isAirplaying =
      (this.video as any)?.webkitCurrentPlaybackTargetIsWireless || false;
    this.setAirplaying(isAirplaying);
  };

  //////////////////////////////////
  // Video Control event handlers //
  //////////////////////////////////

  handlePlayPauseControlClick = () => {
    if (this.state.paused) {
      this.play();
    } else {
      this.pause();
    }
  };

  handleRewatchControlClick = () => {
    if (this.props.playlist) {
      // Ensure controls are not shown
      this.hideControls();
      this.clearControlsTimer();

      this.props.onTimeUpdate?.(0);
      this.setPlaylistPosition(SESSION_VIDEO_PLAYLIST_POSITION);
    } else {
      this.seek(0);
      this.play();
    }

    this.props.onReplay?.();
  };

  handleCaptionControlClick = () => {
    this.toggleCaptions();
  };

  handlePlaybackSpeedControlChange = (newPlaybackRate: number) => {
    this.setPlaybackRate(newPlaybackRate);
  };

  handleVolumeControlChange = (newVolume: number) => {
    this.setVolume(newVolume);
  };

  handleIncreasePlaybackSpeedControlClick = () => {
    this.increasePlaybackRate();
  };

  handleDecreasePlaybackSpeedControlClick = () => {
    this.decreasePlaybackRate();
  };

  handleAirplayControlClick = () => {
    try {
      (this.video as any)?.webkitShowPlaybackTargetPicker();
    } catch (error) {}
  };

  handlePipControlClick = () => {
    this.setViewMode(MediaPlayerViewMode.freestyle);
  };

  handleFullscreenControlClick = () => {
    this.setViewMode(
      this.state.viewMode === MediaPlayerViewMode.fullscreen
        ? MediaPlayerViewMode.inline
        : MediaPlayerViewMode.fullscreen,
    );
  };

  handleGoBackwardControlClick = throttle(() => {
    this.backward();
  }, DEFAULT_THROTTLE);

  handleGoForwardControlClick = throttle(() => {
    this.forward();
  }, DEFAULT_THROTTLE);

  handlePreventHidingControls = throttle(() => {
    this.clearControlsTimer();
  }, DEFAULT_THROTTLE);

  handleSeek = () => {
    this.pause();
  };

  handleSeeked = (newTime: number) => {
    this.seek(newTime);

    // TODO only resume if the user didn't pause
    this.play();
  };

  handleInitialUnmute = () => {
    this.muted = false;
    this.hideControls();
    this.setState({ hasUnmutedAutoplay: true });
  };

  handleTrackLoad = () => {
    this.setCaptionPosition();
    this.setCaptionMode();
  };

  ///////////////
  // Rendering //
  ///////////////

  renderCaptionTracks() {
    const captions = this.props.captions ?? this.currentPlaylistItem?.captions;

    if (!this.state.captionsEnabled || !captions) return null;

    return captions?.map(({ src, lang, isDefault }) => {
      return (
        <Track
          key={src}
          src={src}
          srcLang={lang}
          isDefault={isDefault}
          onLoad={this.handleTrackLoad}
        />
      );
    });
  }

  renderControls() {
    const { hasStarted, hasUnmutedAutoplay, muted, waiting } = this.state;

    if (hasStarted && muted && !hasUnmutedAutoplay) {
      return (
        <div className="Video__unmute" onClick={this.handleInitialUnmute}>
          <Button invert icon="volume-xmark" iconVariant="solid">
            <Translation>
              {(t) => t("control.unmute", {}, "Unmute")}
            </Translation>
          </Button>
        </div>
      );
    }

    const controls =
      this.videoType === VideoPlaylistEntryType.BUMPER ? (
        <MinimalVideoControls
          onFullscreenControlClick={this.handleFullscreenControlClick}
          onPlayPauseControlClick={this.handlePlayPauseControlClick}
          onReplayControlClick={this.handleRewatchControlClick}
        />
      ) : (
        <VideoControls
          onCaptionControlClick={this.handleCaptionControlClick}
          onPlaybackControlSpeedChange={this.handlePlaybackSpeedControlChange}
          onVolumeChange={this.handleVolumeControlChange}
          onAirplayControlClick={this.handleAirplayControlClick}
          onFullscreenControlClick={this.handleFullscreenControlClick}
          onPipControlClick={this.handlePipControlClick}
          onGoBackwardControlClick={this.handleGoBackwardControlClick}
          onPlayPauseControlClick={this.handlePlayPauseControlClick}
          onGoForwardControlClick={this.handleGoForwardControlClick}
          preventHidingControls={this.handlePreventHidingControls}
        />
      );

    return (
      <>
        <div className="Video__loading" aria-hidden={hasStarted || !waiting}>
          {this.poster ? (
            <img src={this.poster} alt={this.props.title} decoding="async" />
          ) : (
            <Spinner size="lg" invert={true} />
          )}
        </div>

        {controls}

        <VideoShortcuts
          onRequestPlayToggle={this.handlePlayPauseControlClick}
          onRequestFullscreen={this.handleFullscreenControlClick}
          onRequestGoBackward={this.handleGoBackwardControlClick}
          onRequestGoForward={this.handleGoForwardControlClick}
          onRequestIncreaseSpeed={this.handleIncreasePlaybackSpeedControlClick}
          onRequestDecreaseSpeed={this.handleDecreasePlaybackSpeedControlClick}
        />
      </>
    );
  }

  render() {
    const { chapters, children, contextRef, title } = this.props;
    const { viewMode } = this.state;
    const context: MediaPlayerContextValue = {
      ...this.state,
      chapters: chapters ?? null,
      mediaElement: this.video,
      // @ts-ignore
      pause: this.pause,
      play: this.play,
      scrub: this.handleSeek,
      seek: this.handleSeeked,
      setCaptions: this.toggleCaptions,
      setPlaybackRate: this.setPlaybackRate,
      setVolume: this.setVolume,
      setViewMode: this.setViewMode,
      setViewModeFullscreenSlides: this.setViewModeFullscreenSlides,
      setViewModeFullscreenWindowPosition:
        this.setViewModeFullscreenWindowPosition,
      setSeeking: this.setSeeking,
      setSeekingTime: this.setSeekingTime,
    };

    if (contextRef) {
      contextRef.current = context;
    }

    return (
      <MediaPlayerContext.Provider value={context}>
        <div
          ref={this.ref}
          className={clsx("Video", {
            "Video--active": this.state.userActivity,
            "Video--loading": this.state.isLoading,
            "Video--has-started": this.state.hasStarted,
            [`Video--${MediaPlayerViewMode[viewMode]}`]: viewMode,
            "Video--fullscreen-window":
              this.state.viewMode == MediaPlayerViewMode.fullscreen &&
              this.state.viewModeFullscreenSlides,
          })}
          tabIndex={-1}
          onMouseMove={this.handleMouseMove}
          onMouseUp={this.handleMouseUp}
          onKeyDown={this.handleKeyDown}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        >
          <VideoElement
            ref={this.mediaElementRef}
            title={title}
            poster={this.poster}
            onCanPlay={this.handleCanPlay}
            onCanPlayThrough={this.handleCanPlayThrough}
            onEnded={this.handleEnded}
            onError={this.handleError}
            onLoadedData={this.handleLoadedData}
            onLoadedMetaData={this.handleLoadedMetaData}
            onLoadStart={this.handleLoadStart}
            onDurationChange={this.handleDurationChange}
            onPause={this.handlePause}
            onPlaying={this.handlePlaying}
            onProgress={this.handleProgress}
            onTimeUpdate={this.handleTimeUpdate}
            onVolumeChange={this.handleVolumeChange}
            onWaiting={this.handleWaiting}
          >
            {children}
            {this.renderCaptionTracks()}
          </VideoElement>

          {this.renderControls()}
        </div>
      </MediaPlayerContext.Provider>
    );
  }
}
